import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Eye, EyeOff, Lock, Search, Shield, User as UserIcon } from 'lucide-react'
import { api } from '@/services/api'
import { useAuth } from '@/store/auth'
import type {
  AdminCatalogEntry,
  AdminClientLimit,
  AdminUser,
  AdminUserPage,
} from '@/services/types'

type Tab = 'people' | 'models' | 'apps' | 'clients'

const TABS: { id: Tab; label: string }[] = [
  { id: 'people', label: 'People' },
  { id: 'models', label: 'Models' },
  { id: 'apps', label: 'Applications' },
  { id: 'clients', label: 'Where models show' },
]

/** Plain English for the enum the server uses. */
const CLIENT_NAMES: Record<AdminClientLimit['client'], string> = {
  WEB: 'Browser',
  MACOS: 'macOS app',
  WINDOWS: 'Windows app',
  LINUX: 'Linux app',
  VSCODE: 'VS Code extension',
}

export function AdminPage() {
  const user = useAuth((s) => s.user)
  const [tab, setTab] = useState<Tab>('people')

  // Hiding the panel is presentation; the server refuses every one of these
  // endpoints without the role. This only avoids showing someone a screen
  // whose every request would answer 403.
  if (user?.role !== 'ADMIN') {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="text-lg font-semibold">Administration</h1>
        <p className="mt-2 text-sm text-content-muted">
          This area is for administrators. If you should have access, ask someone who already
          does, or have your address added to the server&rsquo;s admin list.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl p-6 sm:p-8">
      <h1 className="text-lg font-semibold">Administration</h1>
      <p className="mt-1 text-sm text-content-muted">
        Who can do what, which models and applications are offered, and how many each client
        shows.
      </p>

      <div className="mt-5 flex flex-wrap gap-1 border-b border-border">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
              tab === item.id
                ? 'border-accent font-medium text-content'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'people' && <People currentUserId={user.id} />}
        {tab === 'models' && <Catalog kind="model" />}
        {tab === 'apps' && <Catalog kind="application" />}
        {tab === 'clients' && <Clients />}
      </div>
    </div>
  )
}

function Problem({ message }: { message: string }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
      <AlertTriangle size={15} className="mt-0.5 shrink-0" />
      {message}
    </p>
  )
}

// ------------------------------------------------------------------- people

function People({ currentUserId }: { currentUserId: string }) {
  const [page, setPage] = useState<AdminUserPage | null>(null)
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async (q: string, at: number) => {
    try {
      setPage(await api.adminUsers(q, at))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load accounts')
    }
  }, [])

  useEffect(() => {
    // Debounced: a search that fires per keystroke turns a page of accounts
    // into a request per letter.
    const timer = setTimeout(() => void load(query, 0), 250)
    return () => clearTimeout(timer)
  }, [query, load])

  const setRole = async (person: AdminUser, role: 'USER' | 'ADMIN') => {
    setBusy(person.id)
    try {
      await api.adminSetRole(person.id, role)
      await load(query, page?.page ?? 0)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that role')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      {error && <div className="mb-3">{<Problem message={error} />}</div>}

      <label className="mb-3 flex items-center gap-2 rounded-lg border border-border px-3 py-2">
        <Search size={15} className="shrink-0 text-content-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email"
          className="w-full bg-transparent text-sm outline-none placeholder:text-content-muted"
        />
      </label>

      {!page ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : page.users.length === 0 ? (
        <p className="text-sm text-content-muted">No accounts match that search.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {page.users.map((person) => {
            const isSelf = person.id === currentUserId
            // Two reasons the control is not offered, and they are different
            // enough to say separately: configuration will reinstate the role
            // anyway, or this is the account doing the clicking.
            const reason = person.lockedByConfig
              ? 'Named in the server’s admin email list'
              : isSelf
                ? 'You cannot change your own role'
                : null

            return (
              <li key={person.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
                <span className="shrink-0 text-content-muted">
                  {person.role === 'ADMIN' ? <Shield size={16} /> : <UserIcon size={16} />}
                </span>
                <div className="min-w-[12rem] flex-1">
                  <p className="text-sm font-medium">{person.name}</p>
                  <p className="text-xs text-content-muted">{person.email}</p>
                </div>
                {reason ? (
                  <span
                    title={reason}
                    className="flex shrink-0 items-center gap-1 text-xs text-content-muted"
                  >
                    <Lock size={12} /> {person.role === 'ADMIN' ? 'Administrator' : 'Member'}
                  </span>
                ) : (
                  <select
                    value={person.role}
                    disabled={busy === person.id}
                    onChange={(e) => void setRole(person, e.target.value as 'USER' | 'ADMIN')}
                    aria-label={`Role for ${person.email}`}
                    className="shrink-0 rounded-lg border border-border bg-surface px-2 py-1 text-sm disabled:opacity-50"
                  >
                    <option value="USER">Member</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {page && page.pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page.page === 0}
            onClick={() => void load(query, page.page - 1)}
            className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-content-muted">
            {page.page + 1} of {page.pages} · {page.total} accounts
          </span>
          <button
            type="button"
            disabled={page.page + 1 >= page.pages}
            onClick={() => void load(query, page.page + 1)}
            className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- catalogues

function Catalog({ kind }: { kind: 'model' | 'application' }) {
  const [entries, setEntries] = useState<AdminCatalogEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setEntries(await api.adminCatalog(kind))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the catalogue')
    }
  }, [kind])

  useEffect(() => {
    void load()
  }, [load])

  const change = async (entry: AdminCatalogEntry, patch: { hidden?: boolean; position?: number }) => {
    setBusy(entry.id)
    try {
      await api.adminUpdateEntry(kind, entry.id, patch)
      await load()
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that change')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Moves an entry by rewriting positions from the current order.
   *
   * The list is what people are looking at, so the positions are taken from
   * it rather than from whatever is stored: an entry nobody has placed has no
   * stored position at all, and swapping two of those has to give them one.
   */
  const move = async (index: number, delta: number) => {
    if (!entries) return
    const target = index + delta
    if (target < 0 || target >= entries.length) return
    const reordered = [...entries]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)

    setEntries(reordered)
    setBusy(moved.id)
    try {
      // Only the pair that actually moved is written; rewriting all forty
      // would turn one drag into forty rows that all say "as arranged".
      const from = Math.min(index, target)
      const to = Math.max(index, target)
      for (let i = from; i <= to; i++) {
        await api.adminUpdateEntry(kind, reordered[i].id, { position: i })
      }
      await load()
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reorder those')
      await load()
    } finally {
      setBusy(null)
    }
  }

  if (error && !entries) return <Problem message={error} />
  if (!entries) return <p className="text-sm text-content-muted">Loading…</p>

  const hidden = entries.filter((e) => e.hidden).length

  return (
    <div>
      {error && <div className="mb-3">{<Problem message={error} />}</div>}
      <p className="mb-3 text-xs text-content-muted">
        In the order clients will see them. {entries.length - hidden} shown
        {hidden > 0 && `, ${hidden} hidden`}. Hiding removes an entry from every client; it does
        not delete anything, and what is already installed keeps working.
      </p>

      <ul className="divide-y divide-border rounded-lg border border-border">
        {entries.map((entry, index) => (
          <li
            key={entry.id}
            className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 ${
              entry.hidden ? 'opacity-55' : ''
            }`}
          >
            <span className="w-6 shrink-0 text-right text-xs tabular-nums text-content-muted">
              {index + 1}
            </span>
            <div className="min-w-[12rem] flex-1">
              <p className="text-sm font-medium">{entry.name}</p>
              <p className="font-mono text-[11px] text-content-muted">{entry.id}</p>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                disabled={index === 0 || busy === entry.id}
                onClick={() => void move(index, -1)}
                aria-label={`Move ${entry.name} up`}
                className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                disabled={index === entries.length - 1 || busy === entry.id}
                onClick={() => void move(index, 1)}
                aria-label={`Move ${entry.name} down`}
                className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                disabled={busy === entry.id}
                onClick={() => void change(entry, { hidden: !entry.hidden })}
                title={entry.hidden ? 'Show this to everyone' : 'Hide this from every client'}
                aria-label={entry.hidden ? `Show ${entry.name}` : `Hide ${entry.name}`}
                className="ml-1 flex items-center gap-1 rounded border border-border px-2 py-1 text-xs disabled:opacity-40"
              >
                {entry.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                {entry.hidden ? 'Hidden' : 'Shown'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// --------------------------------------------------------------- client caps

function Clients() {
  const [clients, setClients] = useState<AdminClientLimit[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setClients(await api.adminClients())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load client settings')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async (client: AdminClientLimit, maxModels: number | null) => {
    setBusy(client.client)
    try {
      await api.adminSetClientLimit(client.client, maxModels)
      await load()
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that limit')
    } finally {
      setBusy(null)
    }
  }

  if (error && !clients) return <Problem message={error} />
  if (!clients) return <p className="text-sm text-content-muted">Loading…</p>

  return (
    <div>
      {error && <div className="mb-3">{<Problem message={error} />}</div>}
      <p className="mb-3 text-xs text-content-muted">
        Each client shows the first few models in the order set on the Models tab, so you can see
        exactly which ones a given client will get. Leave a client on “All” to show everything.
      </p>

      <ul className="divide-y divide-border rounded-lg border border-border">
        {clients.map((client) => (
          <li key={client.client} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-3">
            <div className="min-w-[10rem] flex-1">
              <p className="text-sm font-medium">{CLIENT_NAMES[client.client]}</p>
              <p className="text-xs text-content-muted">
                {client.maxModels === null
                  ? `All ${client.available} models`
                  : `First ${Math.min(client.maxModels, client.available)} of ${client.available}`}
              </p>
            </div>
            <select
              value={client.maxModels === null ? 'all' : String(client.maxModels)}
              disabled={busy === client.client}
              onChange={(e) =>
                void save(client, e.target.value === 'all' ? null : Number(e.target.value))
              }
              aria-label={`Models shown in ${CLIENT_NAMES[client.client]}`}
              className="shrink-0 rounded-lg border border-border bg-surface px-2 py-1 text-sm disabled:opacity-50"
            >
              <option value="all">All</option>
              {[3, 5, 8, 10, 15, 20, 30].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </div>
  )
}
